import { useState, useEffect, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { collection, query, where, orderBy, getDocs, type Timestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db } from '../lib/firestore'
import { functions } from '../lib/functions'
import { logGaEvent } from '../lib/analytics'
import { ManagedSeo } from '../components/seo/ManagedSeo'
import {
  HORSE_RIDER_ASSESSMENT_START_EVENT,
  HorseRiderAssessment,
} from '../components/start/HorseRiderAssessment'
import { assets } from '../config/assets'
import { useNextClassDate } from '../hooks/useNextClassDate'
import { useAcceleratorPricing } from '../hooks/useAcceleratorPricing'
import { acceleratorPriceSummary } from '../lib/acceleratorPricing'
import { buildRidersWayStartUrl } from '../lib/ridersWay'

// ─── CHECKOUT ───────────────────────────────────────────────────────────────

interface LeadPayload { firstName: string; email: string; phone: string }
interface LeadResult { url: string | null }

const submitAppTrialLead = httpsCallable<LeadPayload, LeadResult>(
  functions,
  'submitAppTrialLead',
)

type ModalStatus = 'idle' | 'loading' | 'error'

// ─── TYPES ──────────────────────────────────────────────────────────────────

interface UpcomingEvent {
  id: string
  type: string
  title: string
  date: Date
  location: string
}

// ─── PAGE ───────────────────────────────────────────────────────────────────

export function StartPage() {
  const { label: nextClassLabel, date: nextClassDate, loading: nextClassLoading } = useNextClassDate()
  // Device clock is enough for a one-line price here; /accelerator and the
  // checkout function use the server clock.
  const accelPricing = useAcceleratorPricing(
    nextClassLoading || !nextClassDate ? undefined : nextClassDate.getTime(),
    { serverClock: false },
  )
  const [events, setEvents] = useState<UpcomingEvent[]>([])
  const [eventsLoading, setEventsLoading] = useState(true)

  // ── Checkout modal state ──────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [modalStatus, setModalStatus] = useState<ModalStatus>('idle')
  const [modalError, setModalError] = useState('')

  function openModal() {
    logGaEvent('begin_checkout', {
      currency: 'USD',
      value: 19.99,
      items: [{ item_id: 'riders-way-app', item_name: "Rider's Way App", price: 19.99, quantity: 1 }],
      source: 'start_page',
    })
    window.location.assign(buildRidersWayStartUrl('legacy_start_page'))
  }

  function closeModal() {
    setModalOpen(false)
    setFirstName('')
    setEmail('')
    setModalStatus('idle')
    setModalError('')
  }

  async function handleCheckout(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setModalStatus('loading')
    setModalError('')

    try {
      const result = await submitAppTrialLead({
        firstName: firstName.trim(),
        email: email.trim(),
        phone: '',
      })
      const url = result.data.url
      if (url) {
        window.location.href = url
      }
    } catch {
      setModalStatus('error')
      setModalError('Something went wrong. Please try again or email info@advantagehorsemanship.com.')
    }
  }

  const modalDisabled =
    modalStatus === 'loading' ||
    !firstName.trim() ||
    !email.trim()

  useEffect(() => {
    async function fetchEvents() {
      const merged: UpcomingEvent[] = []
      const now = new Date()

      try {
        const mq = query(
          collection(db, 'masterclassEvents'),
          where('active', '==', true),
          orderBy('startDate', 'asc'),
        )
        const mSnap = await getDocs(mq)
        for (const d of mSnap.docs) {
          const data = d.data()
          const startDate = (data.startDate as Timestamp).toDate()
          if (startDate < now) continue
          merged.push({
            id: `mc-${d.id}`,
            type: 'masterclass',
            title: (data.eventName as string) ?? 'Riders Foundation Masterclass',
            date: startDate,
            location: (data.location as string) ?? '',
          })
        }
      } catch { /* index may not exist */ }

      try {
        const sq = query(
          collection(db, 'scheduleEvents'),
          where('active', '==', true),
          orderBy('date', 'asc'),
        )
        const sSnap = await getDocs(sq)
        for (const d of sSnap.docs) {
          const data = d.data()
          const dateStr = (data.date as string) ?? ''
          const eventDate = new Date(dateStr)
          if (eventDate < now) continue
          merged.push({
            id: d.id,
            type: (data.type as string) ?? 'lecture',
            title: (data.title as string) ?? '',
            date: eventDate,
            location: (data.location as string) ?? '',
          })
        }
      } catch { /* collection may not exist */ }

      merged.sort((a, b) => a.date.getTime() - b.date.getTime())
      setEvents(merged.slice(0, 3))
      setEventsLoading(false)
    }
    fetchEvents()
  }, [])

  return (
    <>
      <ManagedSeo path="/start" />

      {/* ════════════════════════════════════════════════════════════
          SECTION 1 — HERO
          ════════════════════════════════════════════════════════════ */}
      <section className="bg-navy">
        {/* Logo */}
        <div className="pt-5 pb-4 flex justify-center">
          <img
            src={assets.logos.primaryNav}
            alt="Advantage Horsemanship"
            width={454}
            height={72}
            style={{ height: 48, width: 'auto' }}
          />
        </div>

        {/* Full-width photo */}
        <div className="w-full overflow-hidden" style={{ maxHeight: 520 }}>
          <img
            src={assets.scott.groundworkHaltering}
            srcSet={assets.scott.groundworkHalteringSrcSet}
            sizes="100vw"
            alt="Scott Purdum working groundwork with a horse"
            width={1200}
            height={800}
            className="w-full object-cover object-top"
            style={{ minHeight: 380 }}
            fetchPriority="high"
          />
        </div>

        {/* Headline block */}
        <div className="px-5 sm:px-8 py-12 lg:py-16 max-w-3xl mx-auto text-center">
          <h1 className="font-heading text-[48px] sm:text-6xl lg:text-7xl text-white leading-[1.05] mb-5">
            Your Horse Is Not<br className="sm:hidden" /> the Problem.
          </h1>
          <p className="font-body text-base sm:text-lg text-white/60 leading-relaxed mb-6 max-w-xl mx-auto">
            Most riders are working on the wrong thing. Scott Purdum helps you fix the real issue.
          </p>
          <p className="font-body text-sm text-gold tracking-wide">
            Horse Behaviorist. Rider Confidence Coach. 20+ years. Every discipline.
          </p>
          <a
            href="#horse-rider-assessment"
            onClick={() => {
              logGaEvent('assessment_entry_click', { placement: 'start_page_hero' })
              window.dispatchEvent(new Event(HORSE_RIDER_ASSESSMENT_START_EVENT))
            }}
            className="mt-8 inline-flex min-h-[56px] w-full items-center justify-center border-2 border-gold px-9 font-body text-sm font-bold uppercase tracking-wider text-gold transition-colors hover:bg-gold hover:text-navy sm:w-auto"
          >
            Find My Starting Point
          </a>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          SECTION 2 — PAIN POINTS
          ════════════════════════════════════════════════════════════ */}
      <section className="bg-white py-16 lg:py-24 px-5 sm:px-8">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-heading text-4xl text-navy text-center font-semibold mb-12">
            Sound familiar?
          </h2>

          <div className="space-y-8">
            {[
              { bold: 'You freeze up.', sub: 'Your mind goes blank the moment something feels off and you lose confidence fast.' },
              { bold: 'Your horse ignores you.', sub: 'You ask and nothing happens. Or worse, you get the opposite of what you wanted.' },
              { bold: 'Every ride feels like a gamble.', sub: 'You never know what you are going to get. Confidence evaporates the moment things go sideways.' },
            ].map(({ bold, sub }) => (
              <div key={bold} className="border-l-4 border-gold pl-6 py-1">
                <p className="font-heading text-2xl text-navy font-semibold mb-1">{bold}</p>
                <p className="font-body text-sm text-navy/60 leading-relaxed">{sub}</p>
              </div>
            ))}
          </div>

          {/* Gold divider */}
          <div className="mt-14 border-t-2 border-gold/30" />
        </div>
      </section>

      <HorseRiderAssessment />

      {/* ════════════════════════════════════════════════════════════
          SECTION 3 — THE APP (primary CTA)
          ════════════════════════════════════════════════════════════ */}
      <section style={{ backgroundColor: '#C8A84B' }} className="py-16 lg:py-24 px-5 sm:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <p
            className="font-body text-xs font-bold tracking-[0.2em] uppercase mb-4"
            style={{ color: '#0A1628' }}
          >
            Start Here
          </p>
          <h2
            className="font-heading font-semibold leading-tight mb-5"
            style={{ fontSize: 42, color: '#0A1628' }}
          >
            The Rider's Way App
          </h2>
          <p className="font-body text-base leading-relaxed mb-8 max-w-lg mx-auto" style={{ color: 'rgba(10,22,40,0.75)' }}>
            If you are just finding Scott, this is where every rider starts. AI
            coaching, weekly guided rides, and a personalized plan built around
            your horse and your situation.
          </p>

          <p className="mb-2">
            <span className="font-body text-2xl font-bold" style={{ color: '#0A1628' }}>$19.99/month</span>
          </p>
          <p className="font-body text-sm mb-8" style={{ color: 'rgba(10,22,40,0.55)' }}>
            7-day free trial. No credit card until day 8.
          </p>

          <button
            onClick={openModal}
            className="inline-flex items-center justify-center font-body text-sm font-bold tracking-wider uppercase min-h-[56px] px-12 w-full sm:w-auto transition-opacity hover:opacity-90"
            style={{ backgroundColor: '#0A1628', color: '#FFFFFF' }}
          >
            Start Your Free Trial
          </button>
          <p className="font-body text-xs mt-4" style={{ color: 'rgba(10,22,40,0.45)' }}>
            Cancel anytime. No long-term commitment.
          </p>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          SECTION 4 — LIVE COACHING
          ════════════════════════════════════════════════════════════ */}
      <section className="bg-white py-16 lg:py-24 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-4xl text-navy text-center font-semibold mb-3">
            Ready for live coaching with Scott?
          </h2>
          <p className="font-body text-sm text-navy/50 text-center mb-12">
            Join Scott directly. Real feedback on your real situation.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Accelerator */}
            <div
              className="bg-white p-7 flex flex-col"
              style={{ borderLeft: '3px solid #0A1628', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
            >
              <p className="font-body text-[10px] font-bold tracking-wider uppercase text-navy mb-3">
                Live Weekly Coaching
              </p>
              <h3 className="font-heading text-2xl text-navy font-semibold mb-2">
                Confidence Accelerator Class
              </h3>
              <p className="font-body text-xs text-gold font-semibold mb-3">
                Next class: {nextClassLabel ?? 'Coming soon'}
              </p>
              <p className="font-body text-sm text-navy/60 leading-relaxed mb-4 flex-1">
                You know what you want with your horse. You just cannot seem to
                get there consistently. The Accelerator gives you direct
                coaching on your specific situation, accountability to keep
                moving, and a community of riders doing the same work.
              </p>
              <p className="font-body text-sm font-semibold text-navy mb-5">
                {acceleratorPriceSummary(accelPricing)}
              </p>
              <Link
                to="/accelerator"
                className="inline-flex items-center justify-center font-body text-sm font-bold tracking-wider uppercase border-2 border-navy text-navy min-h-[48px] w-full hover:bg-navy hover:text-white transition-colors"
              >
                Learn More
              </Link>
            </div>

            {/* Horse Sense */}
            <div
              className="bg-white p-7 flex flex-col"
              style={{ borderLeft: '3px solid #0A1628', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
            >
              <p className="font-body text-[10px] font-bold tracking-wider uppercase text-navy mb-3">
                Ongoing Live Coaching
              </p>
              <h3 className="font-heading text-2xl text-navy font-semibold mb-2">
                Horse Sense Coaching
              </h3>
              <p className="font-body text-xs text-gold font-semibold mb-3">
                Join any Monday
              </p>
              <p className="font-body text-sm text-navy/60 leading-relaxed mb-4 flex-1">
                Most riders treat the symptoms. Horse Sense teaches you to read
                the root cause. You will learn how horses actually think, what
                they are responding to, and how to use that knowledge to fix
                any problem with any horse.
              </p>
              <p className="font-body text-sm font-semibold text-navy mb-5">
                $500 / 2 months
                <span className="text-navy/40 font-normal"> or $2,500/year</span>
              </p>
              <Link
                to="/horse-sense"
                className="inline-flex items-center justify-center font-body text-sm font-bold tracking-wider uppercase border-2 border-navy text-navy min-h-[48px] w-full hover:bg-navy hover:text-white transition-colors"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          SECTION 5 — SCHEDULE
          ════════════════════════════════════════════════════════════ */}
      <section className="bg-navy py-16 lg:py-24 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-4xl text-white text-center font-semibold mb-10">
            Where is Scott next?
          </h2>
          {eventsLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : events.length === 0 ? (
            <p className="font-body text-sm text-white/40 text-center py-6">
              No upcoming events scheduled. Check back soon.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {events.map((ev) => (
                <div
                  key={ev.id}
                  className="bg-white p-5 rounded"
                  style={{
                    borderLeft: `3px solid ${
                      ev.type === 'masterclass' ? '#C8A84B'
                        : ev.type === 'lecture' ? '#C8A84B'
                          : '#6b7280'
                    }`,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  }}
                >
                  <span className={`inline-block font-body text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 mb-3 ${
                    ev.type === 'masterclass'
                      ? 'bg-gold text-navy'
                      : ev.type === 'lecture'
                        ? 'bg-navy text-white'
                        : 'border border-gray-400 text-gray-500'
                  }`}>
                    {ev.type}
                  </span>
                  <h3 className="font-body text-sm font-semibold text-navy mb-1 leading-snug">
                    {ev.title}
                  </h3>
                  <p className="font-body text-xs text-navy/50">
                    {ev.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                  <p className="font-body text-xs text-navy/50">
                    {ev.location}
                  </p>
                </div>
              ))}
            </div>
          )}
          <div className="text-center mt-8">
            <Link
              to="/schedule"
              className="font-body text-sm text-gold hover:opacity-80 transition-opacity"
            >
              View full schedule
            </Link>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          SECTION 6 — TESTIMONIAL
          ════════════════════════════════════════════════════════════ */}
      <section className="bg-white py-16 lg:py-24 px-5 sm:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <div
            className="font-heading text-gold select-none leading-none mb-2"
            style={{ fontSize: 120 }}
            aria-hidden="true"
          >
            &ldquo;
          </div>
          <p className="font-heading italic text-navy leading-relaxed mb-8" style={{ fontSize: 22 }}>
            My mare Josie used to pin her ears every time I picked up the reins.
            Two months into the app I realized I was the one creating the
            tension. Now she meets me at the gate.
          </p>
          <p className="font-body text-sm font-semibold text-navy">
            Sarah Mitchell
          </p>
          <p className="font-body text-xs text-navy/40">
            Bozeman, Montana
          </p>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          SECTION 7 — FINAL CTA
          ════════════════════════════════════════════════════════════ */}
      <section className="bg-navy py-16 lg:py-24 px-5 sm:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-semibold leading-tight mb-5">
            Seven days. No charge. No obligation.
          </h2>
          <p className="font-body text-base text-white/60 mb-10">
            Start building the communication your horse has been waiting for.
          </p>
          <button
            onClick={openModal}
            className="inline-flex items-center justify-center font-body text-sm font-bold tracking-wider uppercase min-h-[56px] px-12 w-full sm:w-auto transition-opacity hover:opacity-90"
            style={{ backgroundColor: '#C8A84B', color: '#0A1628' }}
          >
            Start Your Free Trial
          </button>

          {/* App store buttons */}
          <p className="font-body text-xs text-white/40 mt-10 mb-4">
            Already have the app? Download it here.
          </p>
          <div className="flex flex-row justify-center items-center gap-4">
            <a
              href="https://apps.apple.com/us/app/riders-way/id6752657327"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={assets.app.badgeAppStoreWhite}
                alt="Download on the App Store"
                width={162}
                height={48}
                loading="lazy"
                className="h-12 w-auto min-w-[135px] object-contain"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.coachscott.app&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={assets.app.badgeGooglePlay}
                alt="Get it on Google Play"
                width={162}
                height={48}
                loading="lazy"
                className="h-12 w-auto"
              />
            </a>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CHECKOUT MODAL
          ════════════════════════════════════════════════════════════ */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal()
          }}
        >
          <div className="bg-white w-full max-w-md">
            <div className="bg-navy px-8 py-6 flex items-center justify-between">
              <h2 className="font-heading text-2xl text-white">
                Start Your Free Trial
              </h2>
              <button
                onClick={closeModal}
                className="text-white/60 hover:text-white transition-colors"
                aria-label="Close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCheckout} className="px-8 py-8">
              <p className="font-body text-sm text-navy/70 mb-6">
                Enter your details and you will be taken directly to a secure
                checkout to start your 7-day free trial.
              </p>

              <div className="space-y-4 mb-6">
                <div>
                  <label
                    className="block font-body text-xs font-semibold text-navy/60 uppercase tracking-wide mb-1"
                    htmlFor="start-first-name"
                  >
                    First Name *
                  </label>
                  <input
                    id="start-first-name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    autoComplete="given-name"
                    className="w-full border border-navy/20 px-4 py-3 font-body text-navy focus:outline-none focus:border-navy"
                    style={{ fontSize: '16px' }}
                    placeholder="Your first name"
                  />
                </div>

                <div>
                  <label
                    className="block font-body text-xs font-semibold text-navy/60 uppercase tracking-wide mb-1"
                    htmlFor="start-email"
                  >
                    Email Address *
                  </label>
                  <input
                    id="start-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full border border-navy/20 px-4 py-3 font-body text-navy focus:outline-none focus:border-navy"
                    style={{ fontSize: '16px' }}
                    placeholder="your@email.com"
                  />
                </div>

              </div>

              {modalStatus === 'error' && modalError && (
                <p className="font-body text-sm text-red-600 mb-4">
                  {modalError}
                </p>
              )}

              <button
                type="submit"
                disabled={modalDisabled}
                style={{
                  backgroundColor: modalDisabled ? '#a08940' : '#C8A84B',
                  color: '#0A1628',
                  fontWeight: '700',
                  padding: '16px 32px',
                  fontSize: '16px',
                  border: 'none',
                  cursor: modalDisabled ? 'not-allowed' : 'pointer',
                  width: '100%',
                  minHeight: '52px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                {modalStatus === 'loading'
                  ? 'Redirecting to checkout...'
                  : 'Proceed to Secure Checkout'}
              </button>

              <p className="font-body text-xs text-navy/40 mt-3 text-center">
                Powered by Stripe . Secure checkout
              </p>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
